<template>
  <div class="dashboard-container">
    <!-- 页面标题：解码后的门店名称 -->
    <div class="page-header">
      <h2>{{ decodedStoreName }} - 员工数据排名</h2>
      <button @click="goBack" class="back-btn">← 返回门店列表</button>
    </div>

    <!-- 当日统计容器（员工维度） -->
    <div class="stats-group">
      <h3>当日统计（员工）</h3>
      <div class="chart-card">
        <h3>当日到店人数排名</h3>
        <div id="staffTodayArrivalChart" class="chart-container"></div>
        <div v-if="todayArrivalLoading" class="status-tip">
          <span class="loading-spinner"></span> 数据加载中...
        </div>
        <div v-if="!todayArrivalLoading && !todayArrivalError && todayArrivalEmpty" class="status-tip">
          📊 暂无当日员工到店数据
        </div>
        <div v-if="todayArrivalError" class="status-tip error">
          ⚠️ 当日员工到店数据加载失败，请刷新页面重试
        </div>
      </div>

      <div class="chart-card">
        <h3>当日上传次数排名</h3>
        <div id="staffTodayUploadChart" class="chart-container"></div>
        <div v-if="todayUploadLoading" class="status-tip">
          <span class="loading-spinner"></span> 数据加载中...
        </div>
        <div v-if="!todayUploadLoading && !todayUploadError && todayUploadEmpty" class="status-tip">
          📊 暂无当日员工上传数据
        </div>
        <div v-if="todayUploadError" class="status-tip error">
          ⚠️ 当日员工上传数据加载失败，请刷新页面重试
        </div>
      </div>
    </div>

    <!-- 本月统计容器（员工维度） -->
    <div class="stats-group">
      <h3>本月统计（员工）</h3>
      <div class="chart-card">
        <h3>本月到店人数排名</h3>
        <div id="staffMonthArrivalChart" class="chart-container"></div>
        <div v-if="monthArrivalLoading" class="status-tip">
          <span class="loading-spinner"></span> 数据加载中...
        </div>
        <div v-if="!monthArrivalLoading && !monthArrivalError && monthArrivalEmpty" class="status-tip">
          📊 暂无本月员工到店数据
        </div>
        <div v-if="monthArrivalError" class="status-tip error">
          ⚠️ 本月员工到店数据加载失败，请刷新页面重试
        </div>
      </div>

      <div class="chart-card">
        <h3>本月上传次数排名</h3>
        <div id="staffMonthUploadChart" class="chart-container"></div>
        <div v-if="monthUploadLoading" class="status-tip">
          <span class="loading-spinner"></span> 数据加载中...
        </div>
        <div v-if="!monthUploadLoading && !monthUploadError && monthUploadEmpty" class="status-tip">
          📊 暂无本月员工上传数据
        </div>
        <div v-if="monthUploadError" class="status-tip error">
          ⚠️ 本月员工上传数据加载失败，请刷新页面重试
        </div>
      </div>
    </div>

    <!-- 新增：上个月统计容器（员工维度） -->
    <div class="stats-group">
      <h3>上个月统计（员工）</h3>
      <div class="chart-card">
        <h3>上个月到店人数排名</h3>
        <div id="staffLastMonthArrivalChart" class="chart-container"></div>
        <div v-if="lastMonthArrivalLoading" class="status-tip">
          <span class="loading-spinner"></span> 数据加载中...
        </div>
        <div v-if="!lastMonthArrivalLoading && !lastMonthArrivalError && lastMonthArrivalEmpty" class="status-tip">
          📊 暂无上个月员工到店数据
        </div>
        <div v-if="lastMonthArrivalError" class="status-tip error">
          ⚠️ 上个月员工到店数据加载失败，请刷新页面重试
        </div>
      </div>

      <div class="chart-card">
        <h3>上个月上传次数排名</h3>
        <div id="staffLastMonthUploadChart" class="chart-container"></div>
        <div v-if="lastMonthUploadLoading" class="status-tip">
          <span class="loading-spinner"></span> 数据加载中...
        </div>
        <div v-if="!lastMonthUploadLoading && !lastMonthUploadError && lastMonthUploadEmpty" class="status-tip">
          📊 暂无上个月员工上传数据
        </div>
        <div v-if="lastMonthUploadError" class="status-tip error">
          ⚠️ 上个月员工上传数据加载失败，请刷新页面重试
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, defineProps, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router' // 新增：导入useRoute获取路由参数
import request from '@/util/request'

// 接收路由参数（门店ID+名称 + 新增monthType区分本月/上个月）
const props = defineProps({
  storeId: { type: [String, Number], required: true },
  storeName: { type: String, required: true }
})

// 新增：获取路由中的monthType参数（从门店列表跳转时传递）
const route = useRoute()
const monthType = route.query.monthType || 'current' // current=本月，last=上个月

// 解码门店名称（解决乱码）
const decodedStoreName = computed(() => decodeURIComponent(props.storeName))

const router = useRouter()
const goBack = () => router.back() // 返回上一页

// ========== 图表实例与状态 ==========
// 当日图表
const staffTodayArrivalChart = ref(null)
const staffTodayUploadChart = ref(null)
const todayArrivalLoading = ref(false)
const todayUploadLoading = ref(false)
const todayArrivalError = ref(false)
const todayUploadError = ref(false)
const todayArrivalEmpty = ref(false)
const todayUploadEmpty = ref(false)

// 本月图表
const staffMonthArrivalChart = ref(null)
const staffMonthUploadChart = ref(null)
const monthArrivalLoading = ref(false)
const monthUploadLoading = ref(false)
const monthArrivalError = ref(false)
const monthUploadError = ref(false)
const monthArrivalEmpty = ref(false)
const monthUploadEmpty = ref(false)

// 新增：上个月图表状态
const staffLastMonthArrivalChart = ref(null)
const staffLastMonthUploadChart = ref(null)
const lastMonthArrivalLoading = ref(false)
const lastMonthUploadLoading = ref(false)
const lastMonthArrivalError = ref(false)
const lastMonthUploadError = ref(false)
const lastMonthArrivalEmpty = ref(false)
const lastMonthUploadEmpty = ref(false)

// ========== 工具函数 ==========
// 获取今日日期（YYYY-MM-DD）
const getTodayDate = () => {
  const today = new Date()
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
}

// 获取当月第一天（YYYY-MM-01）
const getMonthFirstDay = () => {
  const today = new Date()
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`
}

// 新增：获取上个月第一天（YYYY-MM-01）
const getLastMonthFirstDay = () => {
  const today = new Date()
  const lastMonth = today.getMonth() === 0 ? 11 : today.getMonth() - 1
  const lastMonthYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear()
  const monthStr = String(lastMonth + 1).padStart(2, '0')
  return `${lastMonthYear}-${monthStr}-01`
}

// 初始化ECharts实例（扩展：包含上个月图表）
const initStaffCharts = () => {
  if (!window.echarts) {
    console.error('❌ ECharts未加载')
    // 所有错误状态置为true
    todayArrivalError.value = todayUploadError.value = monthArrivalError.value = monthUploadError.value = true
    lastMonthArrivalError.value = lastMonthUploadError.value = true // 新增
    return
  }

  // 绑定DOM元素
  const todayArrivalDom = document.getElementById('staffTodayArrivalChart')
  const todayUploadDom = document.getElementById('staffTodayUploadChart')
  const monthArrivalDom = document.getElementById('staffMonthArrivalChart')
  const monthUploadDom = document.getElementById('staffMonthUploadChart')
  // 新增：上个月图表DOM
  const lastMonthArrivalDom = document.getElementById('staffLastMonthArrivalChart')
  const lastMonthUploadDom = document.getElementById('staffLastMonthUploadChart')

  // 初始化原有图表
  staffTodayArrivalChart.value = todayArrivalDom ? window.echarts.init(todayArrivalDom) : null
  staffTodayUploadChart.value = todayUploadDom ? window.echarts.init(todayUploadDom) : null
  staffMonthArrivalChart.value = monthArrivalDom ? window.echarts.init(monthArrivalDom) : null
  staffMonthUploadChart.value = monthUploadDom ? window.echarts.init(monthUploadDom) : null
  // 新增：初始化上个月图表
  staffLastMonthArrivalChart.value = lastMonthArrivalDom ? window.echarts.init(lastMonthArrivalDom) : null
  staffLastMonthUploadChart.value = lastMonthUploadDom ? window.echarts.init(lastMonthUploadDom) : null
}

// 窗口resize适配（扩展：包含上个月图表）
const handleResize = () => {
  setTimeout(() => {
    // 原有图表resize
    staffTodayArrivalChart.value?.resize()
    staffTodayUploadChart.value?.resize()
    staffMonthArrivalChart.value?.resize()
    staffMonthUploadChart.value?.resize()
    // 新增：上个月图表resize
    staffLastMonthArrivalChart.value?.resize()
    staffLastMonthUploadChart.value?.resize()
  }, 100)
}

// ========== 图表渲染函数 ==========
// 当日到店排名（原有）
const renderStaffTodayArrivalChart = (data) => {
  if (!staffTodayArrivalChart.value) return
  const staffNames = data.map(item => item.staff_name)
  const counts = data.map(item => item.today_count)

  const option = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', formatter: (p) => `${p[0].name}: ${p[0].value} 人` },
    grid: { left: '3%', right: '4%', bottom: '2%', containLabel: true },
    xAxis: {
      type: 'category', data: staffNames,
      axisLabel: { rotate: 45, interval: 0, fontSize: 12 },
      axisTick: { alignWithLabel: true }
    },
    yAxis: { type: 'value', name: '到店人数', min: 0 },
    series: [{
      type: 'bar', data: counts,
      itemStyle: { color: '#409EFF', borderRadius: [4, 4, 0, 0] },
      label: { show: true, position: 'top', fontSize: 12, color: '#333' },
      barWidth: '60%'
    }]
  }
  staffTodayArrivalChart.value.setOption(option)
}

// 当日上传排名（原有）
const renderStaffTodayUploadChart = (data) => {
  if (!staffTodayUploadChart.value) return
  const staffNames = data.map(item => item.staff_name)
  const counts = data.map(item => item.upload_count)

  const option = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', formatter: (p) => `${p[0].name}: ${p[0].value} 次` },
    grid: { left: '3%', right: '4%', bottom: '2%', containLabel: true },
    xAxis: {
      type: 'category', data: staffNames,
      axisLabel: { rotate: 45, interval: 0, fontSize: 12 },
      axisTick: { alignWithLabel: true }
    },
    yAxis: { type: 'value', name: '上传次数', min: 0 },
    series: [{
      type: 'bar', data: counts,
      itemStyle: { color: '#67C23A', borderRadius: [4, 4, 0, 0] },
      label: { show: true, position: 'top', fontSize: 12, color: '#333' },
      barWidth: '60%'
    }]
  }
  staffTodayUploadChart.value.setOption(option)
}

// 本月到店排名（原有）
const renderStaffMonthArrivalChart = (data) => {
  if (!staffMonthArrivalChart.value) return
  const staffNames = data.map(item => item.staff_name)
  const counts = data.map(item => item.month_count)

  const option = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', formatter: (p) => `${p[0].name}: ${p[0].value} 人` },
    grid: { left: '3%', right: '4%', bottom: '2%', containLabel: true },
    xAxis: {
      type: 'category', data: staffNames,
      axisLabel: { rotate: 45, interval: 0, fontSize: 12 },
      axisTick: { alignWithLabel: true }
    },
    yAxis: { type: 'value', name: '本月到店人数', min: 0 },
    series: [{
      type: 'bar', data: counts,
      itemStyle: { color: '#409EFF', borderRadius: [4, 4, 0, 0] },
      label: { show: true, position: 'top', fontSize: 12, color: '#333' },
      barWidth: '60%'
    }]
  }
  staffMonthArrivalChart.value.setOption(option)
}

// 本月上传排名（原有）
const renderStaffMonthUploadChart = (data) => {
  if (!staffMonthUploadChart.value) return
  const staffNames = data.map(item => item.staff_name)
  const counts = data.map(item => item.month_upload_count)

  const option = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', formatter: (p) => `${p[0].name}: ${p[0].value} 次` },
    grid: { left: '3%', right: '4%', bottom: '2%', containLabel: true },
    xAxis: {
      type: 'category', data: staffNames,
      axisLabel: { rotate: 45, interval: 0, fontSize: 12 },
      axisTick: { alignWithLabel: true }
    },
    yAxis: { type: 'value', name: '本月上传次数', min: 0 },
    series: [{
      type: 'bar', data: counts,
      itemStyle: { color: '#67C23A', borderRadius: [4, 4, 0, 0] },
      label: { show: true, position: 'top', fontSize: 12, color: '#333' },
      barWidth: '60%'
    }]
  }
  staffMonthUploadChart.value.setOption(option)
}

// 新增：上个月到店排名渲染
const renderStaffLastMonthArrivalChart = (data) => {
  if (!staffLastMonthArrivalChart.value) return
  const staffNames = data.map(item => item.staff_name)
  const counts = data.map(item => item.month_count) // 后端返回字段和本月一致

  const option = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', formatter: (p) => `${p[0].name}: ${p[0].value} 人` },
    grid: { left: '3%', right: '4%', bottom: '2%', containLabel: true },
    xAxis: {
      type: 'category', data: staffNames,
      axisLabel: { rotate: 45, interval: 0, fontSize: 12 },
      axisTick: { alignWithLabel: true }
    },
    yAxis: { type: 'value', name: '上个月到店人数', min: 0 },
    series: [{
      type: 'bar', data: counts,
      itemStyle: { color: '#909399', borderRadius: [4, 4, 0, 0] }, // 灰色区分上个月
      label: { show: true, position: 'top', fontSize: 12, color: '#333' },
      barWidth: '60%',
      emphasis: { itemStyle: { color: '#737980' } }
    }]
  }
  staffLastMonthArrivalChart.value.setOption(option)
}

// 新增：上个月上传排名渲染
const renderStaffLastMonthUploadChart = (data) => {
  if (!staffLastMonthUploadChart.value) return
  const staffNames = data.map(item => item.staff_name)
  const counts = data.map(item => item.month_upload_count) // 后端返回字段和本月一致

  const option = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', formatter: (p) => `${p[0].name}: ${p[0].value} 次` },
    grid: { left: '3%', right: '4%', bottom: '2%', containLabel: true },
    xAxis: {
      type: 'category', data: staffNames,
      axisLabel: { rotate: 45, interval: 0, fontSize: 12 },
      axisTick: { alignWithLabel: true }
    },
    yAxis: { type: 'value', name: '上个月上传次数', min: 0 },
    series: [{
      type: 'bar', data: counts,
      itemStyle: { color: '#E6A23C', borderRadius: [4, 4, 0, 0] }, // 橙色区分上个月
      label: { show: true, position: 'top', fontSize: 12, color: '#333' },
      barWidth: '60%',
      emphasis: { itemStyle: { color: '#d48806' } }
    }]
  }
  staffLastMonthUploadChart.value.setOption(option)
}

// ========== 数据请求函数 ==========
// 当日到店数据（原有）
const fetchStaffTodayArrivalData = async () => {
  todayArrivalLoading.value = true
  todayArrivalError.value = todayArrivalEmpty.value = false
  try {
    const res = await request.get('/api/daodian/staff-ranking/', {
      stat_type: 'today_arrival',
      store_id: props.storeId,
      today_date: getTodayDate()
    })

    if (res?.data?.code !== 200) throw new Error(res?.data?.errorInfo || '数据获取失败')
    const staffData = res.data.data || []

    todayArrivalEmpty.value = staffData.length === 0 || staffData.every(item => item.today_count === 0)
    if (!todayArrivalEmpty.value) renderStaffTodayArrivalChart(staffData)
  } catch (err) {
    console.error('当日到店数据失败:', err.message)
    todayArrivalError.value = true
  } finally {
    todayArrivalLoading.value = false
  }
}

// 当日上传数据（原有）
const fetchStaffTodayUploadData = async () => {
  todayUploadLoading.value = true
  todayUploadError.value = todayUploadEmpty.value = false
  try {
    const res = await request.get('/api/imgaa/screenshots/staff-ranking/', {
      stat_type: 'staff_upload_ranking',
      store_id: props.storeId,
      today_date: getTodayDate()
    })

    if (res?.data?.code !== 200) throw new Error(res?.data?.errorInfo || '数据获取失败')
    const staffData = res.data.data || []

    todayUploadEmpty.value = staffData.length === 0 || staffData.every(item => item.upload_count === 0)
    if (!todayUploadEmpty.value) renderStaffTodayUploadChart(staffData)
  } catch (err) {
    console.error('当日上传数据失败:', err.message)
    todayUploadError.value = true
  } finally {
    todayUploadLoading.value = false
  }
}

// 本月到店数据（原有）
const fetchStaffMonthArrivalData = async () => {
  monthArrivalLoading.value = true
  monthArrivalError.value = monthArrivalEmpty.value = false
  try {
    const res = await request.get('/api/daodian/staff-ranking/', {
      stat_type: 'month_arrival',
      store_id: props.storeId,
      month_date: getMonthFirstDay()
    })

    if (res?.data?.code !== 200) throw new Error(res?.data?.errorInfo || '数据获取失败')
    const staffData = res.data.data || []

    monthArrivalEmpty.value = staffData.length === 0 || staffData.every(item => item.month_count === 0)
    if (!monthArrivalEmpty.value) renderStaffMonthArrivalChart(staffData)
  } catch (err) {
    console.error('本月到店数据失败:', err.message)
    monthArrivalError.value = true
  } finally {
    monthArrivalLoading.value = false
  }
}

// 本月上传数据（原有）
const fetchStaffMonthUploadData = async () => {
  monthUploadLoading.value = true
  monthUploadError.value = monthUploadEmpty.value = false
  try {
    const res = await request.get('/api/imgaa/screenshots/staff-ranking/', {
      stat_type: 'staff_month_upload_ranking',
      store_id: props.storeId,
      month_date: getMonthFirstDay()
    })

    if (res?.data?.code !== 200) throw new Error(res?.data?.errorInfo || '数据获取失败')
    const staffData = res.data.data || []

    monthUploadEmpty.value = staffData.length === 0 || staffData.every(item => item.month_upload_count === 0)
    if (!monthUploadEmpty.value) renderStaffMonthUploadChart(staffData)
  } catch (err) {
    console.error('本月上传数据失败:', err.message)
    monthUploadError.value = true
  } finally {
    monthUploadLoading.value = false
  }
}

// 新增：上个月到店数据请求
const fetchStaffLastMonthArrivalData = async () => {
  lastMonthArrivalLoading.value = true
  lastMonthArrivalError.value = lastMonthArrivalEmpty.value = false
  try {
    const res = await request.get('/api/daodian/staff-ranking/', {
      stat_type: 'month_arrival', // 复用本月接口，仅修改month_date
      store_id: props.storeId,
      month_date: getLastMonthFirstDay() // 传递上个月第一天
    })

    if (res?.data?.code !== 200) throw new Error(res?.data?.errorInfo || '上个月到店数据获取失败')
    const staffData = res.data.data || []

    lastMonthArrivalEmpty.value = staffData.length === 0 || staffData.every(item => item.month_count === 0)
    if (!lastMonthArrivalEmpty.value) renderStaffLastMonthArrivalChart(staffData)
  } catch (err) {
    console.error('上个月到店数据失败:', err.message)
    lastMonthArrivalError.value = true
  } finally {
    lastMonthArrivalLoading.value = false
  }
}

// 新增：上个月上传数据请求
const fetchStaffLastMonthUploadData = async () => {
  lastMonthUploadLoading.value = true
  lastMonthUploadError.value = lastMonthUploadEmpty.value = false
  try {
    const res = await request.get('/api/imgaa/screenshots/staff-ranking/', {
      stat_type: 'staff_month_upload_ranking', // 复用本月接口，仅修改month_date
      store_id: props.storeId,
      month_date: getLastMonthFirstDay() // 传递上个月第一天
    })

    if (res?.data?.code !== 200) throw new Error(res?.data?.errorInfo || '上个月上传数据获取失败')
    const staffData = res.data.data || []

    lastMonthUploadEmpty.value = staffData.length === 0 || staffData.every(item => item.month_upload_count === 0)
    if (!lastMonthUploadEmpty.value) renderStaffLastMonthUploadChart(staffData)
  } catch (err) {
    console.error('上个月上传数据失败:', err.message)
    lastMonthUploadError.value = true
  } finally {
    lastMonthUploadLoading.value = false
  }
}

// 批量请求所有数据（扩展：包含上个月）
const fetchAllStaffData = async () => {
  try {
    await Promise.all([
      fetchStaffTodayArrivalData(),
      fetchStaffTodayUploadData(),
      fetchStaffMonthArrivalData(),
      fetchStaffMonthUploadData(),
      // 新增：上个月数据请求
      fetchStaffLastMonthArrivalData(),
      fetchStaffLastMonthUploadData()
    ])
  } catch (err) {
    console.error('批量加载数据失败:', err)
  }
}

// ========== 生命周期 ==========
onMounted(() => {
  initStaffCharts()
  fetchAllStaffData()
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  // 销毁原有图表实例
  staffTodayArrivalChart.value?.dispose()
  staffTodayUploadChart.value?.dispose()
  staffMonthArrivalChart.value?.dispose()
  staffMonthUploadChart.value?.dispose()
  // 新增：销毁上个月图表实例
  staffLastMonthArrivalChart.value?.dispose()
  staffLastMonthUploadChart.value?.dispose()

  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.dashboard-container {
  padding: 20px;
  background-color: #fff;
  min-height: calc(100vh - 40px);
  box-sizing: border-box;
}

/* 页面标题样式 */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}
.page-header h2 {
  font-size: 20px;
  color: #303133;
  margin: 0;
  font-weight: 500;
}
.back-btn {
  padding: 6px 12px;
  background-color: #409EFF;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.back-btn:hover {
  background-color: #2d8cf0;
}

/* 统计组样式 */
.stats-group {
  margin-bottom: 30px;
}
.stats-group h3 {
  font-size: 18px;
  margin-bottom: 15px;
  border-left: 3px solid #409EFF;
  padding-left: 10px;
}
/* 新增：上个月统计标题区分色 */
.stats-group:nth-child(4) h3 {
  border-left-color: #909399;
}

/* 图表卡片样式 */
.chart-card {
  position: relative;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 11px;
  margin-bottom: 10px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
}
.chart-card h3 {
  font-size: 16px;
  color: #303133;
  margin: 0 0 10px 0;
  font-weight: 500;
}

/* 图表容器样式 */
.chart-container {
  width: 100% !important;
  height: 220px !important;
  box-sizing: border-box;
}

/* 状态提示样式 */
.status-tip {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #666;
  font-size: 14px;
  text-align: center;
}
.status-tip.error {
  color: #f56c6c;
}

/* 加载动画 */
.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ddd;
  border-top-color: #409EFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 6px;
  vertical-align: middle;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 响应式适配 */
@media (max-width: 768px) {
  .dashboard-container { padding: 10px; }
  .chart-container { height: 250px !important; }
  .status-tip { font-size: 12px; }
  .chart-card h3 { font-size: 15px; }
  .page-header h2 { font-size: 18px; }
}
@media (max-width: 480px) {
  .dashboard-container { padding: 8px; }
  .chart-container { height: 200px !important; }
  .chart-card { padding: 10px; }
  .page-header { flex-direction: column; align-items: flex-start; gap: 10px; }
}
</style>